interface VoiceSession {
  id: symbol;
  finish: () => Promise<void>;
  start: () => Promise<void>;
}

let activeVoiceSession: VoiceSession | null = null;
let transitionQueue: Promise<void> = Promise.resolve();
let recoveryQueue: Promise<void> = Promise.resolve();

/**
 * 串行执行原生语音恢复操作。activateVoiceSession 会等待该队列，
 * 避免旧会话的全局 cancel 迟到后误取消新录音。
 */
export function queueVoiceSessionRecovery(
  recovery: () => Promise<unknown>
) {
  const task = recoveryQueue.then(async () => {
    try {
      await recovery();
    } catch (_error) {
      // 恢复操作只用于尽力清理原生状态，失败不能阻塞后续语音会话。
    }
  });
  recoveryQueue = task;
  return task;
}

export async function waitForVoiceSessionRecovery() {
  while (true) {
    const pendingRecovery = recoveryQueue;
    await pendingRecovery;
    if (pendingRecovery === recoveryQueue) return;
  }
}

/**
 * 串行切换全局语音会话：先等待原生恢复、完整结束旧会话，再启动新会话。
 */
export function activateVoiceSession(session: VoiceSession) {
  const transition = transitionQueue.then(async () => {
    await waitForVoiceSessionRecovery();
    if (activeVoiceSession?.id === session.id) return;

    const previousSession = activeVoiceSession;
    if (previousSession) {
      await previousSession.finish();
      // finish 可能触发新的 cancel 恢复，启动下一会话前必须再次等待。
      await waitForVoiceSessionRecovery();
      if (activeVoiceSession?.id === previousSession.id) {
        activeVoiceSession = null;
      }
    }

    activeVoiceSession = session;
    try {
      await session.start();
    } catch (error) {
      releaseVoiceSession(session.id);
      throw error;
    }
  });

  transitionQueue = transition.catch(() => undefined);
  return transition;
}

/** 仅允许当前持有者释放会话，避免旧异步任务误释放新录音。 */
export function releaseVoiceSession(sessionId: symbol) {
  if (activeVoiceSession?.id === sessionId) {
    activeVoiceSession = null;
  }
}
